import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { Shield } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useEffect, useState } from 'react';
import { UserType, UserRoles } from '@/lib/firebase/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  userType: UserType;
}

export default function ProtectedRoute({ children, userType }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [userTypeLoading, setUserTypeLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<UserRoles | null>(null);

  useEffect(() => {
    async function checkUserRoles() {
      if (!user) {
        setUserTypeLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserRoles(userDoc.data().roles as UserRoles);
        }
      } catch (error) {
        console.error('Error checking user roles:', error);
      } finally {
        setUserTypeLoading(false);
      }
    }

    checkUserRoles();
  }, [user]);

  if (loading || userTypeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (!userRoles) {
    // User document doesn't have roles - show error message
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Setup Required</h2>
          <p className="text-gray-600 mb-4">
            Your account doesn't have the required permissions to access this page.
            This might be because your account setup is incomplete.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            User ID: {user.uid}
          </p>
          <div className="space-y-2">
            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90"
            >
              Go to Home
            </button>
            <button
              onClick={() => {
                // Force reload to re-check auth
                window.location.reload();
              }}
              className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // For creator routes, require creator role
  if (userType === 'creator' && !userRoles.creator) {
    return <Navigate to="/dashboard" replace />;
  }

  // For member routes, always allow if they have member role
  if (userType === 'member' && !userRoles.member) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
}